import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

export const DEAD_LETTERS_QUERY_KEY = "healthlinkDeadLetters";

/**
 * Inbound Healthlink messages that could not be parsed.
 *
 * KIN-1596 stopped a poison message being redelivered on every poll: it is captured,
 * acknowledged back to Healthlink, and set aside with the reason it failed. The backend then
 * excludes these rows from every other read path — deliberately, because a message we could
 * not parse must never be consumed as though we had — which is why this endpoint is the only
 * way to see one at all.
 *
 * The response is the bare DTO, not the `ApiResponse` {status, data} envelope: this
 * controller is not registered with ApiExceptionHandler, unlike the newer eLab/MN-CMS ones.
 * Do not add an unwrapping adapter.
 */
export const useDeadLetters = ({ page = 1, size = 10, enabled = true } = {}) => {
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: [DEAD_LETTERS_QUERY_KEY, tenantVal, page, size],
    enabled: !!enabled && !!tenantVal,
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/healthlink/messages/dead-letters?page=${page}&size=${size}`
      );

      return response.data;
    },
    meta: { errorMessage: "Error fetching dead-lettered Healthlink messages" },
  });
};
